import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  createNgModule,
  DoCheck,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

@Component({
  selector: 'rxnt-dynamic',
  templateUrl: './rxnt-dynamic.component.html',
  styleUrl: './rxnt-dynamic.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RxntDynamicComponent implements OnChanges, AfterViewInit, DoCheck {
  // https://github.com/wittyprogramming/lazy-load-component-angular13/tree/master/src/app
  @Input() module: Type<any> | null = null;
  @Input() component: Type<any> | null = null;
  @Input() inputs: Record<string, unknown> | null = null;
  @Output() onInstanceCreated = new EventEmitter<any>();

  @ViewChild('dynamicComponent', { read: ViewContainerRef })
  dynamicComponent: ViewContainerRef | null = null;

  private componentRef: ComponentRef<any> | null = null;
  private appliedKeys = new Set<string>();

  constructor(
    private injector: Injector,
    private ref: ChangeDetectorRef,
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['component']) {
      if (this.dynamicComponent != null) {
        // On first render, this will not fire, because ViewChild is not yet available
        // When @Inputs are changed, it is safe to rerender, because ViewChild is already available
        this.renderDynamic();
      }
    }
  }

  ngAfterViewInit(): void {
    // ViewChild is guaranteed to be available here, so it's always safe to render
    this.renderDynamic();
  }

  ngDoCheck(): void {
    if (this.componentRef != null && this.inputs != null) {
      this.syncInputs(this.componentRef, this.inputs);
    }
  }

  private syncInputs(componentRef: ComponentRef<any>, inputs: Record<string, unknown>) {
    const currentKeys = new Set(Object.keys(inputs));

    for (const key of this.appliedKeys) {
      if (!currentKeys.has(key)) {
        componentRef.setInput(key, undefined);
      }
    }

    for (const key of currentKeys) {
      componentRef.setInput(key, inputs[key]);
    }

    this.appliedKeys = currentKeys;
  }

  private renderDynamic() {
    if (this.dynamicComponent == null) {
      throw new Error('Dynamic component should be defined when renderDynamic is called.');
    }

    if (this.module != null && this.component != null) {
      this.lazyLoad(this.dynamicComponent, this.module, this.component);
    } else {
      // if either module or component was not provided, render a blank
      this.dynamicComponent.clear();
      this.componentRef = null;
      this.appliedKeys.clear();
    }

    this.ref.detectChanges();
  }

  private lazyLoad<T, C>(lazyComponent: ViewContainerRef, ngModule: Type<T>, componentType: Type<C>) {
    const moduleRef = createNgModule(ngModule, this.injector);
    lazyComponent.clear();
    this.componentRef = lazyComponent.createComponent(componentType, { ngModuleRef: moduleRef });
    this.componentRef.onDestroy(() => moduleRef.destroy());
    this.appliedKeys.clear();

    if (this.inputs != null) {
      this.syncInputs(this.componentRef, this.inputs);
    }

    this.onInstanceCreated.emit(this.componentRef.instance);
  }
}
